import type { SrCustomerStatus } from '@plato/data/starrocks/types';
import { SrSalesOrderType } from '@plato/data/starrocks/types';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatOrderId } from '../../../lib/order-helpers.ts';
import type { SearchEntityItemData } from '../../../server/types/search-types.ts';
import { GlobalSearchTab, SearchEntity } from '../../../server/types/search-types.ts';
import { api } from '../../../utils/api.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { useDrawers } from '../../drawers/all-drawers.tsx';

type SearchEntityKeys = keyof SearchEntityItemData;

export type SearchEntityItem<T extends SearchEntityKeys = SearchEntityKeys> = {
    entity: SearchEntity;
    id: string;
    name: string;
    subName?: string;
    extra?: string;
    status?: SrCustomerStatus;
    original: SearchEntityItemData[T];
};

type UseSearchEntityDataProps = {
    entities: SearchEntity[];
    search: string;
    selectedTabKey: GlobalSearchTab;
};

type UseSearchEntityDataReturn = {
    data: Map<SearchEntity, SearchEntityItem[]>;
    requestId: string | undefined;
    limit: number;
    offset: number;
    isLoading: boolean;
    isFetched: boolean;
};

export const useSearchEntityData = ({
    entities,
    search,
    selectedTabKey,
}: UseSearchEntityDataProps): UseSearchEntityDataReturn => {
    const appConfig = useAppConfig();

    const getFilteredEntitiesByTab = (
        selectedTabKey: GlobalSearchTab,
        entities: SearchEntity[],
    ): SearchEntity[] => {
        switch (selectedTabKey) {
            case GlobalSearchTab.All:
                return entities;
            case GlobalSearchTab.Customers:
                return [SearchEntity.Customer];
            case GlobalSearchTab.Orders:
                return [SearchEntity.SalesOrder];
            case GlobalSearchTab.Quotes:
                return [SearchEntity.SalesQuote];
            case GlobalSearchTab.Products:
                return [SearchEntity.Product];
            default:
                return [];
        }
    };

    const filteredEntities: SearchEntity[] = getFilteredEntitiesByTab(selectedTabKey, entities);
    const limit = selectedTabKey === GlobalSearchTab.All ? 6 : 20;
    const offset = 0;

    const {
        data,
        isLoading: isLoadingQuery,
        isFetched: isFetchedQuery,
    } = api.platoUser.search.search.useQuery(
        {
            search,
            limit,
            entities: filteredEntities,
        },
        {
            enabled: Boolean(search),
        },
    );

    const filteredItems = data?.items ?? [];

    const customers = filteredItems.filter(
        (item): item is SearchEntityItemData[SearchEntity.Customer] => item.entity === SearchEntity.Customer,
    );

    const orders = filteredItems.filter(
        (item): item is SearchEntityItemData[SearchEntity.SalesOrder] =>
            item.entity === SearchEntity.SalesOrder,
    );

    const quotes = filteredItems.filter(
        (item): item is SearchEntityItemData[SearchEntity.SalesQuote] =>
            item.entity === SearchEntity.SalesQuote,
    );

    const products = filteredItems.filter(
        (item): item is SearchEntityItemData[SearchEntity.Product] => item.entity === SearchEntity.Product,
    );

    const dataInOrder = new Map<SearchEntity, SearchEntityItem[]>();

    for (const entity of filteredEntities) {
        switch (entity) {
            case SearchEntity.Customer: {
                dataInOrder.set(
                    SearchEntity.Customer,
                    customers.map((customer) => ({
                        entity: SearchEntity.Customer,
                        id: customer.id,
                        name: customer.name,
                        subName:
                            [customer.city, customer.country_code].filter(Boolean).join(', ') || undefined,
                        extra: customer.erp_id,
                        status: customer.status,
                        original: customer,
                    })),
                );
                break;
            }
            case SearchEntity.Product: {
                dataInOrder.set(
                    SearchEntity.Product,
                    products.map((product) => ({
                        entity: SearchEntity.Product,
                        id: product.product_variant_id,
                        name: product.product_variant_name,
                        extra: product.product_variant_id,
                        original: product,
                    })),
                );

                break;
            }
            case SearchEntity.SalesOrder: {
                dataInOrder.set(
                    SearchEntity.SalesOrder,
                    orders.map((order) => ({
                        entity: SearchEntity.SalesOrder,
                        id: order.erp_id,
                        name: formatOrderId(appConfig, order.erp_id),
                        original: order,
                        extra: [order.customer_id, order.customer_name].filter(Boolean).join(' ') || '',
                    })),
                );
                break;
            }
            case SearchEntity.SalesQuote: {
                dataInOrder.set(
                    SearchEntity.SalesQuote,
                    quotes.map((quote) => ({
                        entity: SearchEntity.SalesQuote,
                        id: quote.erp_id,
                        name: formatOrderId(appConfig, quote.erp_id),
                        original: quote,
                        extra: [quote.customer_id, quote.customer_name].filter(Boolean).join(' ') || '',
                    })),
                );
                break;
            }
        }
    }

    return {
        data: dataInOrder,
        requestId: data?.request_id,
        limit,
        offset,
        isLoading: isLoadingQuery && Boolean(search),
        isFetched: isFetchedQuery,
    };
};

type SearchEntityNavigationItem = { erp_id: string; entity: SearchEntity };

type UseSearchEntityNavigationReturn = {
    navigateToEntity: (item: SearchEntityNavigationItem) => void;
};

export const useSearchEntityNavigation = (): UseSearchEntityNavigationReturn => {
    const navigate = useNavigate();
    const { openProductDetails, openOrderQuoteDetails } = useDrawers();

    const navigateToEntity = useCallback(
        (item: SearchEntityNavigationItem) => {
            const { entity, erp_id } = item;

            switch (entity) {
                case SearchEntity.Customer:
                    void navigate(`/customers/${erp_id}`);
                    break;
                case SearchEntity.Product:
                    openProductDetails(erp_id);
                    break;
                case SearchEntity.SalesOrder:
                case SearchEntity.SalesQuote:
                    openOrderQuoteDetails(
                        entity === SearchEntity.SalesOrder ? SrSalesOrderType.ORDER : SrSalesOrderType.QUOTE,
                        erp_id,
                    );
                    break;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    return {
        navigateToEntity,
    };
};
